// Cantidad de una línea (comercial ronda 1): TODA venta se registra en UNIDAD BASE. La presentación
// secundaria (caja, display) es solo una AYUDA DE DIGITACIÓN: «5c» o «5 cj» con RTU 9 se guarda como
// 45 UN. Inventario, reserva, DTE y despacho siguen viendo la cantidad base. El backend decide igual:
// aquí solo se convierte lo digitado y se avisa antes de enviar (múltiplos de envase).
import type { Presentacion } from '../api/contratos';
import { decimal, entradaDecimal } from '../comun/formato';

export interface Equivalencia {
  /** Código de la presentación digitada (CJ, CJ12…). */
  codigo: string;
  cantidad: string;
  factor: string;
}

export type CantidadLeida =
  { ok: true; base: string; equivalencia: Equivalencia | null } | { ok: false; error: string };

/** Redondeo a 6 decimales sin ruido binario («0.30000000000000004» → «0.3»). */
function texto(n: number): string {
  return String(Number(n.toFixed(6)));
}

/** Presentaciones vendibles distintas de la base, de menor a mayor factor (las del asistente RTU). */
export function secundarias(presentaciones: readonly Presentacion[]): Presentacion[] {
  return presentaciones
    .filter((p) => !p.es_base && p.vendible && p.activa && Number(p.factor_a_base) > 1)
    .sort((a, b) => Number(a.factor_a_base) - Number(b.factor_a_base));
}

/**
 * Lee lo digitado: «45» (unidad base), «5c» / «5 cj» / «5CJ12» (presentación secundaria cuyo código
 * empieza así), «45un». La coma decimal se acepta. Devuelve la cantidad en unidad base.
 */
export function leerCantidad(
  entrada: string,
  presentaciones: readonly Presentacion[],
  unidadBase: string,
): CantidadLeida {
  const m = /^\s*(\d+(?:[.,]\d+)?)\s*([a-zA-Z][a-zA-Z0-9]*)?\s*$/.exec(entrada);
  if (!m)
    return { ok: false, error: 'Cantidad inválida: un número, o número y unidad (5c = 5 cajas)' };
  const numero = entradaDecimal(m[1]!);
  if (!numero || Number(numero) <= 0)
    return { ok: false, error: 'La cantidad debe ser mayor que cero' };
  const unidad = (m[2] ?? '').toUpperCase();
  const base = unidadBase.toUpperCase();
  if (!unidad || unidad === base || base.startsWith(unidad))
    return { ok: true, base: texto(Number(numero)), equivalencia: null };
  const opciones = secundarias(presentaciones);
  const elegida =
    opciones.find((p) => p.codigo.toUpperCase() === unidad) ??
    opciones.find((p) => p.codigo.toUpperCase().startsWith(unidad));
  if (!elegida)
    return {
      ok: false,
      error: opciones.length
        ? `«${unidad}» no es una unidad del producto: use ${[base, ...opciones.map((o) => o.codigo)].join(', ')}`
        : `El producto se vende solo en ${base}`,
    };
  return {
    ok: true,
    base: texto(Number(numero) * Number(elegida.factor_a_base)),
    equivalencia: {
      codigo: elegida.codigo,
      cantidad: texto(Number(numero)),
      factor: texto(Number(elegida.factor_a_base)),
    },
  };
}

/** null si la cantidad base respeta el envase cerrado; si no, el aviso con las cantidades válidas. */
export function problemaMultiplo(base: string, multiplo: string | null | undefined): string | null {
  if (!multiplo || !(Number(multiplo) > 0)) return null;
  const m = Number(multiplo);
  const resto = Number(base) / m;
  if (Math.abs(resto - Math.round(resto)) < 1e-9) return null;
  const abajo = Math.floor(resto) * m;
  const arriba = Math.ceil(resto) * m;
  return `Se vende solo en múltiplos de ${decimal(String(m))} (${[abajo, arriba]
    .filter((x) => x > 0)
    .map((x) => decimal(String(x)))
    .join(' o ')}…)`;
}

/** «5 CJ × 9 = 45 UN»: la equivalencia que se muestra junto a la cantidad. */
export function textoEquivalencia(e: Equivalencia, base: string, unidadBase: string): string {
  return `${decimal(e.cantidad)} ${e.codigo} × ${decimal(e.factor)} = ${decimal(base)} ${unidadBase}`;
}
